import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'Sobre a JampaBit Informática - Suporte Técnico em TI João Pessoa',
  description: 'Conheça a JampaBit Informática: empresa de suporte técnico em TI em João Pessoa, PB. Atendimento domiciliar, empresarial e para gamers com qualidade e preço justo.',
  url: `${import.meta.env.VITE_SITE_URL}/sobre`,
  mainEntity: {
    '@type': 'Organization',
    name: 'JampaBit Informática',
    description: 'Empresa de suporte técnico em TI em João Pessoa, Paraíba. Formatação, montagem de PCs gamers, limpeza, suporte remoto e consultoria em TI.',
    url: import.meta.env.VITE_SITE_URL,
    telephone: '+55-83-99175-5663',
    email: 'jampabit@gmail.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'João Pessoa',
      addressRegion: 'PB',
      addressCountry: 'BR',
    },
    foundingDate: '2020',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 5 },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: '500',
    },
  },
};

export default function SobrePage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-sobre';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-sobre');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-[#1a3a4a] pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 border border-[#00c9a7]/40 text-[#00c9a7] text-xs font-semibold px-4 py-2 rounded-full mb-5 md:mb-6">
            <i className="ri-information-line" />
            Sobre Nós
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-5">
            Conheça a <span className="text-[#00c9a7]">JampaBit</span>
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto">
            Tecnologia com confiança, atendimento humanizado e soluções que funcionam de verdade.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="rounded-2xl overflow-hidden h-64 md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=friendly%20IT%20technician%20smiling%20while%20working%20on%20computer%20repair%20in%20professional%20workshop%2C%20tools%20and%20components%20on%20desk%2C%20teal%20and%20navy%20color%20accents%2C%20warm%20professional%20atmosphere%2C%20Jo%C3%A3o%20Pessoa%20Brazil%20tech%20shop&width=700&height=500&seq=about001&orientation=landscape"
              alt="JampaBit - Nossa História"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 text-[#00c9a7] text-xs font-semibold uppercase tracking-widest mb-4">
              <span className="w-8 h-px bg-[#00c9a7]" />
              Nossa História
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0d1b2a] mb-4 md:mb-5 leading-tight">
              Nascemos para Resolver Seus Problemas de TI
            </h2>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-3 md:mb-4">
              A JampaBit Informática nasceu da paixão por tecnologia e do desejo de oferecer suporte técnico de qualidade para as pessoas de João Pessoa e região. Sabemos que um computador com problema pode travar sua vida — seja no trabalho, nos estudos ou no lazer.
            </p>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-3 md:mb-4">
              Por isso, criamos uma empresa focada em atendimento rápido, transparente e com preço justo. Atendemos tanto pessoas físicas quanto empresas, sempre com o mesmo cuidado e dedicação.
            </p>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 md:mb-8">
              Nosso diferencial é simples: <strong className="text-[#0d1b2a]">vamos até você</strong>. Não precisa deslocar seu equipamento — nossa equipe vai ao seu endereço para resolver o problema no conforto da sua casa ou empresa.
            </p>
            <div className="grid grid-cols-3 gap-4 md:gap-6">
              {[
                { value: '500+', label: 'Clientes Atendidos' },
                { value: '5★', label: 'Avaliação Média' },
                { value: '100%', label: 'Satisfação Garantida' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-xl md:text-2xl font-bold text-[#00c9a7]">{s.value}</div>
                  <div className="text-gray-500 text-xs mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission/Vision/Values */}
      <section className="py-12 md:py-16 bg-[#f0faf8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0d1b2a]">Missão, Visão e Valores</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                icon: 'ri-focus-3-line',
                title: 'Missão',
                text: 'Oferecer soluções em informática com qualidade, agilidade e preço justo, tornando a tecnologia acessível para todos os perfis de clientes.',
              },
              {
                icon: 'ri-eye-line',
                title: 'Visão',
                text: 'Ser referência em assistência técnica e consultoria em TI em João Pessoa, reconhecida pela excelência no atendimento e confiança dos clientes.',
              },
              {
                icon: 'ri-heart-line',
                title: 'Valores',
                text: 'Honestidade, transparência, comprometimento com o cliente, qualidade técnica e respeito em cada atendimento realizado.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl bg-[#1a3a4a] mx-auto mb-4 md:mb-5">
                  <i className={`${item.icon} text-[#00c9a7] text-xl md:text-2xl`} />
                </div>
                <h3 className="text-[#0d1b2a] font-bold text-base md:text-lg mb-2 md:mb-3">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Atendimento */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[#00c9a7] text-xs font-semibold uppercase tracking-widest mb-4">
              <span className="w-8 h-px bg-[#00c9a7]" />
              Quem Atendemos
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0d1b2a] mb-5 md:mb-6 leading-tight">
              Pessoa Física e Jurídica
            </h2>
            <div className="space-y-4 md:space-y-5">
              {[
                { icon: 'ri-home-line', title: 'Residencial', desc: 'Atendimento domiciliar para famílias e estudantes. Formatação, limpeza, montagem e suporte remoto.' },
                { icon: 'ri-building-2-line', title: 'Empresarial', desc: 'Suporte técnico para pequenas e médias empresas. Redes, servidores, manutenção preventiva e consultoria.' },
                { icon: 'ri-gamepad-line', title: 'Gamers', desc: 'Montagem de PCs gamers personalizados, upgrades e otimização de desempenho para jogos.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00c9a7]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#00c9a7] text-lg`} />
                  </div>
                  <div>
                    <h3 className="text-[#0d1b2a] font-bold text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden h-64 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=IT%20technician%20visiting%20client%20home%20office%20for%20computer%20repair%20service%2C%20friendly%20professional%20interaction%2C%20laptop%20and%20tools%20on%20desk%2C%20warm%20home%20environment%2C%20teal%20and%20navy%20color%20accents%2C%20Brazil%20tech%20support&width=700&height=450&seq=about002&orientation=landscape"
              alt="Atendimento JampaBit"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
